import type { HttpServiceConfig } from '@/services/httpServices/types';

/**
 * Type for objects that may contain an httpRequestConfig property with an authToken
 */
type WithHttpRequestConfig = {
  httpRequestConfig?: HttpServiceConfig;
};

/**
 * Removes the authToken from an HttpServiceConfig if present
 * @param config The HttpServiceConfig object
 * @returns A new HttpServiceConfig without the authToken
 */
function sanitizeHttpConfig(config?: HttpServiceConfig): HttpServiceConfig | undefined {
  if (config === undefined) {
    return undefined;
  }

  const { authToken, ...rest } = config;
  return rest;
}

/**
 * Creates a sanitized version of params by removing authToken from httpRequestConfig if present
 * @param params The params object which may contain an httpRequestConfig
 * @returns A sanitized copy of the params
 */
function sanitizeParams<T extends WithHttpRequestConfig | undefined>(params: T): T {
  if (params === undefined || params === null) {
    return params;
  }

  if (typeof params !== 'object') {
    return params;
  }

  if (params.httpRequestConfig === undefined) {
    return params;
  }

  return {
    ...params,
    httpRequestConfig: sanitizeHttpConfig(params.httpRequestConfig),
  } as T;
}

/**
 * Creates a query key that is safe to use in caching by removing sensitive data
 * such as authToken from the params
 * @param baseKey The base key parts (e.g., ['profiles', 'list'])
 * @param params The params object which may contain sensitive data
 * @returns A safe query key array with sanitized params
 */
export function createQueryKey<T extends WithHttpRequestConfig>(baseKey: string[], params?: T): Array<string | T | undefined> {
  if (params === undefined) {
    return [...baseKey];
  }
  return [...baseKey, sanitizeParams(params)];
}
